/**
 * DOM adapter: pointer-down. Parses the hit element's `data-handle` string and
 * routes it to the headless gesture engine (`editor-model/gestures`). All drag
 * math and state transitions live there; this file only translates the DOM.
 */

import { AlcoveEdgeName, CornerName, EdgeName, removeFreeElement, removeSegment } from '@kaiizen/kai-floor-plan';
import {
  beginAlcoveWallDrag,
  beginChamferDiagDrag,
  beginEdgeDrag,
  beginFreeMove,
  beginFreeRotate,
  beginFreeSideDrag,
  beginSlideDrag,
  beginTriDrag,
  type EditorMutableState,
  type LineEdgeRef,
  openAddMenu,
  type OuterWhich,
  parseChamferSelection,
  select,
} from '@kaiizen/kai-floor-plan-editor-core';
import { pointerScreenPoint, screenToModelPoint } from './domPoint';

/** Parse a `ls*:...` handle payload into a LineEdgeRef + segment index. */
function parseLineSegHandle(rest: string[]): { ref: LineEdgeRef; si: number } | null {
  const rk = rest[0];
  if (rk === 'pe') return { ref: { k: 'pe', edge: rest[1] as EdgeName }, si: Number(rest[2]) };
  if (rk === 'aw') return { ref: { k: 'aw', edge: rest[1] as EdgeName, storageIndex: Number(rest[2]), wall: rest[3] as AlcoveEdgeName }, si: Number(rest[4]) };
  if (rk === 'rc') return { ref: { k: 'rc', corner: rest[1] as CornerName }, si: Number(rest[2]) };
  if (rk === 'ac') return { ref: { k: 'ac', edge: rest[1] as EdgeName, storageIndex: Number(rest[2]), which: rest[3] as OuterWhich }, si: Number(rest[4]) };
  return null;
}

export function handlePointerDown(
  e: PointerEvent,
  st: EditorMutableState,
  svgEl: SVGSVGElement,
  render: () => void,
): void {
  st.addMenu = null;
  const target = (e.target as Element).closest('[data-handle]');
  if (!target) {
    select(st, { kind: 'none' });
    render();
    return;
  }
  const handle = target.getAttribute('data-handle')!;
  const [type, ...rest] = handle.split(':');
  e.preventDefault();

  // Read-only: the instant mutations (deletes, add menu) don't run at all;
  // gesture handles still dispatch below — their selection side effects are
  // wanted — but the started drag is discarded before it can move anything.
  const readOnly = st.readOnly ?? false;
  if (readOnly && (type === 'ls-d' || type === 'delinner' || type === 'delalcove' || type === 'addbump')) {
    render();
    return;
  }

  const screen = pointerScreenPoint(svgEl, e);
  const model = screenToModelPoint(st.viewport, svgEl, e);
  const s = st.shape;

  switch (type) {
    case 'room':
      select(st, { kind: 'room' });
      break;

    case 'edge':
      beginEdgeDrag(st, rest[0] as EdgeName, { gs: Number(rest[1]), ge: Number(rest[2]) }, screen);
      break;

    case 'tri': {
      const sel = parseChamferSelection(rest);
      if (sel) beginTriDrag(st, sel);
      break;
    }
    case 'diag': {
      const sel = parseChamferSelection(rest);
      if (sel) beginChamferDiagDrag(st, sel, screen);
      break;
    }

    // Alcove body + resize handles are ordinary segment slides on the primary
    // edge — same gesture as doors/windows, alcove-aware clamps inside.
    case 'alcove': {
      const edge = rest[0] as EdgeName, index = Number(rest[1]);
      beginSlideDrag(st, { k: 'pe', edge }, index, 'move', model, { kind: 'alcove', edge, index });
      break;
    }
    case 'alcovewall':
    case 'awgap': {
      const edge = rest[0] as EdgeName, storageIndex = Number(rest[1]), wall = rest[2] as AlcoveEdgeName;
      const gap = type === 'awgap' ? { gs: Number(rest[3]), ge: Number(rest[4]) } : null;
      beginAlcoveWallDrag(st, edge, storageIndex, wall, gap, screen);
      break;
    }

    case 'inner':
      beginFreeMove(st, Number(rest[0]), model);
      break;
    case 'inner-edge':
      beginFreeSideDrag(st, Number(rest[0]), rest[1] as EdgeName, screen);
      break;
    case 'inner-rotate':
      beginFreeRotate(st, Number(rest[0]));
      break;

    case 'ls':
    case 'ls-s':
    case 'ls-e': {
      const parsed = parseLineSegHandle(rest);
      if (!parsed) break;
      const mode = type === 'ls' ? 'move' : type === 'ls-s' ? 'start' : 'end';
      // Body click selects the segment; end handles keep the current selection.
      beginSlideDrag(st, parsed.ref, parsed.si, mode, model, type === 'ls' ? undefined : null);
      break;
    }
    case 'ls-d': {
      // No confirm on deletes — undo restores.
      const parsed = parseLineSegHandle(rest);
      if (parsed) {
        st.shape = removeSegment(st.shape, parsed.ref, parsed.si);
        select(st, { kind: 'none' });
      }
      break;
    }

    case 'delinner':
      st.shape = removeFreeElement(st.shape, Number(rest[0]));
      select(st, { kind: 'none' });
      break;
    case 'delalcove':
      st.shape = removeSegment(st.shape, { k: 'pe', edge: rest[0] as EdgeName }, Number(rest[1]));
      select(st, { kind: 'none' });
      break;

    case 'addbump':
      openAddMenu(st);
      break;
  }
  if (readOnly) {
    st.drag = null;
    st.draftShape = null;
  }
  render();
}
